"use client";

import Link from "next/link";
import { useEffect, useMemo, useState, type ReactNode } from "react";

// ─── design tokens (match GitHub landing page) ───────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@400;600;700;800&display=swap');

  .sdk-root {
    --bg: #0a0a0f;
    --surface: #111118;
    --surface2: #1a1a24;
    --border: rgba(255,255,255,0.08);
    --border2: rgba(255,255,255,0.14);
    --accent: #00e5a0;
    --text: #f0efe8;
    --muted: #7a7a8a;
    --danger: #ff4d6d;
    --warn-bg: rgba(255,180,0,0.07);
    --warn-border: rgba(255,180,0,0.25);
    --warn-text: #f5c842;
    --mono: 'DM Mono', monospace;
    --sans: 'Syne', sans-serif;
    --r-md: 8px;
    --r-lg: 12px;
    --r-xl: 16px;
    font-family: var(--sans);
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    overflow-x: hidden;
    font-size: 16px;
    line-height: 1.6;
  }

  /* grid texture */
  .sdk-root::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px);
    background-size: 48px 48px;
    pointer-events: none;
    z-index: 0;
  }

  .sdk-root * { box-sizing: border-box; }

  /* glow */
  .sdk-glow {
    position: fixed;
    width: 600px; height: 600px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0,229,160,0.04) 0%, transparent 70%);
    top: -100px; right: -100px;
    pointer-events: none; z-index: 0;
  }

  /* ── header ── */
  .sdk-header {
    position: relative; z-index: 1;
    border-bottom: 1px solid var(--border);
    background: rgba(10,10,15,0.9);
    backdrop-filter: blur(12px);
  }

  .sdk-header-inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 3rem 2rem 2.5rem;
  }

  .sdk-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(0,229,160,0.1);
    border: 1px solid rgba(0,229,160,0.25);
    color: var(--accent);
    font-family: var(--mono);
    font-size: 11px;
    padding: 4px 12px;
    border-radius: 2px;
    margin-bottom: 1.5rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .sdk-badge::before {
    content: '';
    width: 6px; height: 6px;
    background: var(--accent);
    border-radius: 50%;
    animation: sdk-pulse 2s infinite;
  }

  @keyframes sdk-pulse {
    0%,100% { opacity:1; transform:scale(1); }
    50% { opacity:.4; transform:scale(.8); }
  }

  .sdk-header-grid {
    display: grid;
    grid-template-columns: 1fr 220px;
    gap: 3rem;
    align-items: end;
  }

  .sdk-h1 {
    font-size: clamp(1.8rem, 4vw, 3rem);
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.05;
    margin-bottom: 1rem;
    color: var(--text);
  }

  .sdk-header-sub {
    font-size: 0.95rem;
    color: var(--muted);
    line-height: 1.7;
    margin-bottom: 1.5rem;
    max-width: 600px;
  }

  .sdk-header-sub a {
    color: var(--accent);
    text-decoration: none;
    border-bottom: 1px solid rgba(0,229,160,0.3);
  }

  .sdk-header-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .sdk-btn-primary {
    display: inline-flex;
    align-items: center;
    background: var(--accent);
    color: #000;
    font-family: var(--mono);
    font-size: 12px;
    font-weight: 500;
    padding: 10px 18px;
    border-radius: var(--r-md);
    text-decoration: none;
    transition: opacity .2s, transform .15s;
    letter-spacing: 0.02em;
    border: none; cursor: pointer;
  }

  .sdk-btn-primary:hover { opacity: .85; transform: translateY(-1px); }

  .sdk-btn-secondary {
    display: inline-flex;
    align-items: center;
    background: transparent;
    color: var(--text);
    font-family: var(--mono);
    font-size: 12px;
    font-weight: 500;
    padding: 10px 18px;
    border-radius: var(--r-md);
    border: 1px solid var(--border2);
    text-decoration: none;
    transition: border-color .2s, transform .15s;
    letter-spacing: 0.02em;
    cursor: pointer;
  }

  .sdk-btn-secondary:hover { border-color: rgba(255,255,255,.3); transform: translateY(-1px); }

  /* header mini cards */
  .sdk-header-cards {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .sdk-mini-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    padding: 12px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .sdk-mini-card-icon { font-size: 1rem; }

  .sdk-mini-card-title {
    font-size: 0.82rem;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 2px;
  }

  .sdk-mini-card-sub {
    font-size: 0.75rem;
    color: var(--muted);
    line-height: 1.4;
  }

  /* ── mobile nav bar ── */
  .sdk-mobile-bar {
    position: sticky;
    top: 0; z-index: 30;
    border-bottom: 1px solid var(--border);
    background: rgba(10,10,15,0.92);
    backdrop-filter: blur(12px);
    padding: 10px 1.5rem;
    display: none;
  }

  .sdk-mobile-bar button {
    background: var(--surface);
    border: 1px solid var(--border2);
    color: var(--text);
    font-family: var(--mono);
    font-size: 12px;
    padding: 8px 16px;
    border-radius: var(--r-md);
    cursor: pointer;
    letter-spacing: 0.04em;
  }

  /* ── layout ── */
  .sdk-layout {
    position: relative; z-index: 1;
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem 2rem 6rem;
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 2rem;
    align-items: start;
  }

  /* ── sidebar ── */
  .sdk-sidebar {
    position: sticky;
    top: 2rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r-xl);
    padding: 1rem;
  }

  .sdk-sidebar-title {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--muted);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 0.75rem;
    padding: 0 4px;
  }

  .sdk-sidebar nav { display: flex; flex-direction: column; gap: 2px; }

  .sdk-nav-link {
    display: block;
    padding: 7px 10px;
    border-radius: var(--r-md);
    font-size: 13px;
    color: var(--muted);
    text-decoration: none;
    transition: color .15s, background .15s;
    border: 1px solid transparent;
  }

  .sdk-nav-link:hover { color: var(--text); background: var(--surface2); }

  .sdk-nav-link.active {
    color: var(--accent);
    background: rgba(0,229,160,0.07);
    border-color: rgba(0,229,160,0.2);
    font-weight: 600;
  }

  /* ── main content ── */
  .sdk-main { display: flex; flex-direction: column; gap: 1rem; min-width: 0; }

  /* ── section card ── */
  .sdk-section {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r-xl);
    padding: 1.75rem 2rem;
    scroll-margin-top: 2rem;
  }

  .sdk-section-eyebrow {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--accent);
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 0.4rem;
  }

  .sdk-section h2 {
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--text);
    margin-bottom: 1rem;
  }

  .sdk-section-body {
    font-size: 0.9rem;
    color: var(--muted);
    line-height: 1.7;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .sdk-section-body p { color: var(--muted); }
  .sdk-section-body strong { color: var(--text); }

  .sdk-section-body code {
    font-family: var(--mono);
    font-size: 12px;
    color: var(--accent);
    background: rgba(0,229,160,0.07);
    padding: 2px 6px;
    border-radius: 3px;
  }

  /* ── code block ── */
  .sdk-code-wrap {
    background: #0d0d14;
    border: 1px solid var(--border2);
    border-radius: var(--r-lg);
    overflow: hidden;
  }

  .sdk-code-bar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: var(--surface2);
    border-bottom: 1px solid var(--border);
  }

  .sdk-dot { width: 9px; height: 9px; border-radius: 50%; }
  .sdk-dot-r { background: #ff5f57; }
  .sdk-dot-y { background: #febc2e; }
  .sdk-dot-g { background: #28c840; }

  .sdk-code-lang {
    margin-left: auto;
    font-family: var(--mono);
    font-size: 10px;
    color: var(--muted);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .sdk-code-wrap pre {
    padding: 1.25rem 1.5rem;
    overflow-x: auto;
    font-family: var(--mono);
    font-size: 12.5px;
    line-height: 1.75;
    color: #c9c5b8;
    margin: 0;
  }

  /* ── step cards grid ── */
  .sdk-steps {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .sdk-step-card {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    padding: 1.25rem;
  }

  .sdk-step-num {
    width: 28px; height: 28px;
    border-radius: 50%;
    background: rgba(0,229,160,0.1);
    border: 1px solid rgba(0,229,160,0.3);
    display: flex; align-items: center; justify-content: center;
    font-family: var(--mono);
    font-size: 11px;
    color: var(--accent);
    margin-bottom: 0.75rem;
    font-weight: 500;
  }

  .sdk-step-card h3 {
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 0.4rem;
  }

  .sdk-step-card p {
    font-size: 0.8rem;
    color: var(--muted);
    line-height: 1.55;
  }

  /* ── alert boxes ── */
  .sdk-alert-warn {
    background: var(--warn-bg);
    border: 1px solid var(--warn-border);
    border-radius: var(--r-lg);
    padding: 1rem 1.25rem;
    font-size: 0.85rem;
    color: var(--warn-text);
    line-height: 1.6;
  }

  .sdk-alert-info {
    background: rgba(0,229,160,0.05);
    border: 1px solid rgba(0,229,160,0.15);
    border-left: 3px solid var(--accent);
    border-radius: var(--r-lg);
    padding: 1rem 1.25rem;
    font-size: 0.85rem;
    color: var(--muted);
    line-height: 1.6;
  }

  .sdk-alert-info strong { color: var(--text); }

  .sdk-alert-neutral {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    padding: 1rem 1.25rem;
    font-size: 0.85rem;
    color: var(--muted);
    line-height: 1.6;
  }

  /* ── flow diagram ── */
  .sdk-flow {
    display: flex;
    flex-direction: column;
    gap: 1px;
    background: var(--border);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    overflow: hidden;
  }

  .sdk-flow-step {
    background: var(--surface2);
    padding: 0.85rem 1.25rem;
    font-size: 0.88rem;
    color: var(--muted);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .sdk-flow-step-accent {
    background: rgba(0,229,160,0.08);
    border-left: 3px solid var(--accent);
    color: var(--accent);
    font-weight: 600;
    font-size: 0.85rem;
  }

  /* ── modules grid ── */
  .sdk-modules {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }

  .sdk-module-card {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    padding: 1.25rem;
    transition: border-color .2s;
  }

  .sdk-module-card:hover { border-color: var(--border2); }

  .sdk-module-card h3 {
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 0.4rem;
  }

  .sdk-module-card p {
    font-size: 0.78rem;
    color: var(--muted);
    margin-bottom: 0.75rem;
    line-height: 1.5;
  }

  .sdk-method {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--accent);
    background: rgba(0,229,160,0.07);
    padding: 3px 8px;
    border-radius: 3px;
    display: block;
    width: fit-content;
    margin-top: 4px;
  }

  /* ── url display ── */
  .sdk-url-box {
    background: #0d0d14;
    border: 1px solid var(--border2);
    border-radius: var(--r-lg);
    overflow: hidden;
  }

  .sdk-url-bar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    background: var(--surface2);
    border-bottom: 1px solid var(--border);
    font-family: var(--mono);
    font-size: 11px;
    color: var(--muted);
  }

  .sdk-url-dot { width: 7px; height: 7px; border-radius: 50%; background: rgba(0,229,160,.4); }

  .sdk-url-text {
    padding: 0.85rem 1.25rem;
    font-family: var(--mono);
    font-size: 12px;
    color: var(--muted);
    word-break: break-all;
    line-height: 1.7;
  }

  .sdk-url-text span { color: var(--accent); }

  /* ── env var list ── */
  .sdk-var-list { display: flex; flex-direction: column; gap: 6px; }

  .sdk-var-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.82rem;
    color: var(--muted);
    line-height: 1.5;
  }

  /* ── rate limits ── */
  .sdk-limits-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .sdk-limit-card {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    padding: 1.25rem;
  }

  .sdk-limit-card-label {
    font-family: var(--mono);
    font-size: 10px;
    color: var(--muted);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 0.75rem;
  }

  .sdk-limit-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    font-size: 0.85rem;
    border-bottom: 1px solid var(--border);
  }

  .sdk-limit-row:last-of-type { border-bottom: none; }
  .sdk-limit-row span:first-child { color: var(--muted); }
  .sdk-limit-val { font-family: var(--mono); color: var(--accent); font-size: 13px; }

  /* error rows */
  .sdk-error-row { padding: 8px 0; border-bottom: 1px solid var(--border); }
  .sdk-error-row:last-child { border-bottom: none; }
  .sdk-error-code { font-family: var(--mono); font-size: 12px; color: var(--danger); }
  .sdk-error-title { font-size: 0.85rem; font-weight: 700; color: var(--text); margin-left: 8px; }
  .sdk-error-desc { font-size: 0.78rem; color: var(--muted); margin-top: 3px; line-height: 1.5; }

  /* ── summary ── */
  .sdk-summary-inner {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    padding: 1.5rem;
  }

  .sdk-summary-tagline {
    font-size: 1.1rem;
    font-weight: 800;
    color: var(--text);
    letter-spacing: -0.01em;
    margin: 0.75rem 0;
  }

  .sdk-summary-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 1.5rem;
  }

  /* ── mobile sheet ── */
  .sdk-sheet-overlay {
    position: fixed;
    inset: 0; z-index: 50;
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(4px);
  }

  .sdk-sheet {
    position: absolute;
    inset-x: 0; bottom: 0;
    max-height: 80vh;
    background: var(--surface);
    border-top: 1px solid var(--border2);
    border-radius: 16px 16px 0 0;
    overflow: hidden;
  }

  .sdk-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--border);
  }

  .sdk-sheet-header-title { font-size: 0.9rem; font-weight: 700; color: var(--text); }
  .sdk-sheet-header-sub { font-size: 0.75rem; color: var(--muted); margin-top: 2px; }

  .sdk-sheet-close {
    background: var(--surface2);
    border: 1px solid var(--border2);
    color: var(--text);
    font-family: var(--mono);
    font-size: 11px;
    padding: 6px 14px;
    border-radius: var(--r-md);
    cursor: pointer;
  }

  .sdk-sheet-nav {
    overflow-y: auto;
    max-height: calc(80vh - 70px);
    padding: 1rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .sdk-sheet-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    text-decoration: none;
    color: var(--text);
    font-size: 0.88rem;
    transition: border-color .15s;
  }

  .sdk-sheet-link:hover { border-color: rgba(0,229,160,.3); }
  .sdk-sheet-link-num { font-family: var(--mono); font-size: 11px; color: var(--muted); }

  /* ── footer ── */
  .sdk-footer {
    position: relative; z-index: 1;
    border-top: 1px solid var(--border);
    padding: 1.5rem 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    font-family: var(--mono);
    font-size: 12px;
    color: var(--muted);
  }

  .sdk-footer a { color: var(--muted); text-decoration: none; margin-left: 1.5rem; }
  .sdk-footer a:hover { color: var(--accent); }

  /* ── responsive ── */
  @media (max-width: 1100px) {
    .sdk-layout { grid-template-columns: 1fr; }
    .sdk-sidebar { display: none; }
    .sdk-mobile-bar { display: block; }
    .sdk-header-grid { grid-template-columns: 1fr; }
    .sdk-header-cards { flex-direction: row; }
  }

  @media (max-width: 640px) {
    .sdk-steps { grid-template-columns: 1fr; }
    .sdk-modules { grid-template-columns: 1fr; }
    .sdk-limits-grid { grid-template-columns: 1fr; }
    .sdk-header-cards { flex-direction: column; }
    .sdk-section { padding: 1.25rem; }
  }
`;

// ─── data ────────────────────────────────────────────────────────────────────

const installCommand = `npm install @crossabc/reloadpi-browse-sdk`;

const backendExample = `import { ShoppiBrowseSDK } from "@crossabc/reloadpi-browse-sdk";

const sdk = new ShoppiBrowseSDK({
  baseUrl: process.env.RELOADPI_API_BASE_URL!,
  apiKey: process.env.RELOADPI_API_KEY!,
  shopUrl: process.env.NEXT_PUBLIC_RELOADPI_SHOP_URL || "https://reloadpi.com",
  referralCode: process.env.NEXT_PUBLIC_RELOADPI_REFERRAL_CODE,
});

export default sdk;`;

const routeExample = `import { NextRequest, NextResponse } from "next/server";
import sdk from "@/lib/reloadpi/sdk";

export async function GET(req: NextRequest) {
  try {
    const { searchParams } = new URL(req.url);
    const country = searchParams.get("country") || undefined;
    const brand   = searchParams.get("brand")   || undefined;
    const limit   = Number(searchParams.get("limit")  || 12);
    const offset  = Number(searchParams.get("offset") || 0);

    const result = await sdk.vouchers.searchOffers({ country, brand, limit, offset });

    return NextResponse.json({
      items: result.items ?? [],
      total: result.total ?? 0,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "Failed to load catalog.";
    return NextResponse.json({ error: message }, { status: 500 });
  }
}`;

const frontendExample = `"use client";
import { useEffect, useState } from "react";

type Offer = { offerId: string; brandName?: string; brand?: string; name?: string; country?: string; };

export default function VoucherGrid() {
  const [items, setItems] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res  = await fetch("/api/catalog/vouchers/offers?country=NG&limit=12", { cache: "no-store" });
        const data = await res.json();
        setItems(data.items ?? []);
      } finally { setLoading(false); }
    }
    load();
  }, []);

  if (loading) return <p>Loading offers…</p>;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((item) => (
        <div key={item.offerId} className="rounded-xl border p-4">
          <h3 className="font-semibold">{item.brandName ?? item.brand ?? item.name ?? item.offerId}</h3>
          {item.country ? <p className="mt-1 text-sm text-stone-500">{item.country}</p> : null}
        </div>
      ))}
    </div>
  );
}`;

const buyLinkExample = `import sdk from "@/lib/reloadpi/sdk";

// Gift card / voucher
const voucherUrl = sdk.getBuyUrl("vouchers", "AMAZON_NG_001");
// → https://reloadpi.com/vouchers/offer/AMAZON_NG_001?ref=PARTNER_ABC

// Mobile top-up
const topupUrl = sdk.getBuyUrl("topups", "TOPUP_OFFER_ID");
// → https://reloadpi.com/topups/offer/TOPUP_OFFER_ID?ref=PARTNER_ABC

// eSIM — MUST use "esims" (plural). Using "esim" (singular) → 404 on ReloadPi.
const esimUrl = sdk.getBuyUrl("esims", "ESIM_OFFER_ID");
// → https://reloadpi.com/esims/offer/ESIM_OFFER_ID?ref=PARTNER_ABC`;

const envExample = `# Server-only — never expose to the browser
RELOADPI_API_BASE_URL=https://shoppi-backend.onrender.com/api/catalog/
RELOADPI_API_KEY=your_private_api_key

# Safe to expose to the browser
NEXT_PUBLIC_RELOADPI_SHOP_URL=https://reloadpi.com
NEXT_PUBLIC_RELOADPI_REFERRAL_CODE=PARTNER_ABC`;

const sections = [
  { id: "install",        label: "Install" },
  { id: "rules",          label: "Rules" },
  { id: "flow",           label: "Flow" },
  { id: "backend",        label: "Backend" },
  { id: "route",          label: "API Route" },
  { id: "frontend",       label: "Frontend" },
  { id: "buy-links",      label: "Buy Links" },
  { id: "modules",        label: "Modules" },
  { id: "env",            label: "Environment" },
  { id: "access",         label: "Access" },
  { id: "limits",         label: "Rate Limits" },
  { id: "troubleshooting",label: "Troubleshooting" },
  { id: "summary",        label: "Summary" },
] as const;

// ─── sub-components ──────────────────────────────────────────────────────────

function CodeBlock({ children, language = "bash" }: { children: string; language?: string }) {
  return (
    <div className="sdk-code-wrap">
      <div className="sdk-code-bar">
        <span className="sdk-dot sdk-dot-r" /><span className="sdk-dot sdk-dot-y" /><span className="sdk-dot sdk-dot-g" />
        <span className="sdk-code-lang">{language}</span>
      </div>
      <pre><code>{children}</code></pre>
    </div>
  );
}

function Sec({ id, eyebrow, title, children }: { id: string; eyebrow: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="sdk-section">
      <div className="sdk-section-eyebrow">{eyebrow}</div>
      <h2>{title}</h2>
      <div className="sdk-section-body">{children}</div>
    </section>
  );
}

function StepCard({ number, title, text }: { number: string; title: string; text: string }) {
  return (
    <div className="sdk-step-card">
      <div className="sdk-step-num">{number}</div>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
}

function useActiveSection(ids: readonly string[], enabled: boolean) {
  const [active, setActive] = useState(ids[0] ?? "");
  useEffect(() => {
    if (!enabled) return;
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const next = visible[0]?.target?.id;
        if (next) setActive((p) => (p === next ? p : next));
      },
      { rootMargin: "-12% 0px -70% 0px", threshold: 0.2 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [enabled, ids]);
  return active;
}

// ─── page ────────────────────────────────────────────────────────────────────

export default function PartnerSdkPage() {
  const sectionIds = useMemo(() => sections.map((s) => s.id), []);
  const isDesktop  = useMediaQuery("(min-width: 1100px)");
  const active     = useActiveSection(sectionIds, isDesktop);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = sheetOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sheetOpen]);

  return (
    <div className="sdk-root">
      <style>{css}</style>
      <div className="sdk-glow" />

      {/* ── HEADER ── */}
      <header className="sdk-header">
        <div className="sdk-header-inner">
          <div className="sdk-badge">Partner SDK</div>
          <div className="sdk-header-grid">
            <div>
              <h1 className="sdk-h1">ReloadPi Browse SDK<br />Integration Guide</h1>
              <p className="sdk-header-sub">
                Use <strong>@crossabc/reloadpi-browse-sdk</strong> to browse gift cards, mobile top-ups,
                and eSIM catalog data in your backend, then display results in your frontend and redirect
                users to ReloadPi to complete purchases.{" "}
                <a href="https://crossabc.github.io/reloadpi-browse-sdk/" target="_blank" rel="noopener noreferrer">
                  Full SDK docs →
                </a>
              </p>
              <div className="sdk-header-actions">
                <a href="#install" className="sdk-btn-primary">Start integration</a>
                <a href="https://crossabc.github.io/reloadpi-browse-sdk/" target="_blank" rel="noopener noreferrer" className="sdk-btn-secondary">SDK docs →</a>
                <a href="#summary" className="sdk-btn-secondary">Quick summary</a>
              </div>
            </div>

            <div className="sdk-header-cards">
              {[
                { icon: "🎁", title: "Gift Cards",  sub: "Browse voucher offers and redirect users to buy." },
                { icon: "📱", title: "Top-ups",     sub: "List top-up offers by country and operator." },
                { icon: "📶", title: "eSIMs",       sub: "Browse plans and route traffic to ReloadPi." },
              ].map((c) => (
                <div key={c.title} className="sdk-mini-card">
                  <div className="sdk-mini-card-icon">{c.icon}</div>
                  <div>
                    <div className="sdk-mini-card-title">{c.title}</div>
                    <div className="sdk-mini-card-sub">{c.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* ── MOBILE BAR ── */}
      <div className="sdk-mobile-bar">
        <button type="button" onClick={() => setSheetOpen(true)}>Contents ↑</button>
      </div>

      {/* ── LAYOUT ── */}
      <div className="sdk-layout">

        {/* sidebar */}
        <aside className="sdk-sidebar">
          <div className="sdk-sidebar-title">Contents</div>
          <nav>
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`} className={`sdk-nav-link${active === s.id ? " active" : ""}`}>
                {s.label}
              </a>
            ))}
          </nav>
        </aside>

        {/* main */}
        <main className="sdk-main">

          <Sec id="install" eyebrow="Step 1" title="Install">
            <p>Install the package in your app or partner integration:</p>
            <CodeBlock language="bash">{installCommand}</CodeBlock>
          </Sec>

          <Sec id="rules" eyebrow="Step 2" title="Dead simple rules">
            <div className="sdk-steps">
              <StepCard number="1" title="Use the SDK on the backend"       text="Initialize and call the SDK from your server, route handlers, or API routes." />
              <StepCard number="2" title="Do not expose private API keys"   text="Keep API keys on the server. The frontend should only call your own API endpoints." />
              <StepCard number="3" title="Fetch catalog from your own route" text="Your frontend should call your own route like /api/catalog/vouchers/offers." />
              <StepCard number="4" title="Redirect users to ReloadPi"       text="Users browse inside your app, then click Buy and complete the purchase on ReloadPi." />
            </div>
            <div className="sdk-alert-warn">
              <strong>Important:</strong> this SDK is for browsing and referral flows. It is read-only.
              You are not building checkout inside your app.
            </div>
          </Sec>

          <Sec id="flow" eyebrow="Step 3" title="Integration flow">
            <p>Recommended integration pattern for Next.js and similar apps:</p>
            <div className="sdk-flow">
              {["Frontend UI (React / Next.js)", "Your API route", "ReloadPi Browse SDK", "ReloadPi API"].map((item) => (
                <div key={item} className="sdk-flow-step">
                  <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--muted)", minWidth: 16 }}>↓</span>
                  {item}
                </div>
              ))}
              <div className="sdk-flow-step sdk-flow-step-accent">
                ↳ User clicks Buy → redirected to ReloadPi to complete purchase
              </div>
            </div>
          </Sec>

          <Sec id="backend" eyebrow="Step 4" title="Backend setup">
            <p>Create one shared SDK instance on the server. A good place is <code>lib/reloadpi/sdk.ts</code>.</p>
            <CodeBlock language="ts">{backendExample}</CodeBlock>
          </Sec>

          <Sec id="route" eyebrow="Step 5" title="API route example">
            <p>Expose your own API route. Your frontend should fetch from this route instead of calling ReloadPi directly.</p>
            <p>Example file: <code>app/api/catalog/vouchers/offers/route.ts</code></p>
            <CodeBlock language="ts">{routeExample}</CodeBlock>
          </Sec>

          <Sec id="frontend" eyebrow="Step 6" title="Frontend fetch example">
            <p>In your client component, fetch from your own route and render the catalog items.</p>
            <CodeBlock language="tsx">{frontendExample}</CodeBlock>
          </Sec>

          <Sec id="buy-links" eyebrow="Step 7" title="Buy link example">
            <p>Use the SDK to generate the final ReloadPi product link with your referral code attached.</p>
            <CodeBlock language="ts">{buyLinkExample}</CodeBlock>
            <div className="sdk-url-box">
              <div className="sdk-url-bar"><div className="sdk-url-dot" /><span>reloadpi.com</span></div>
              <div className="sdk-url-text" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div>https://reloadpi.com/<span>vouchers</span>/offer/<span>OFFER_ID</span>?ref=<span>YOUR_CODE</span></div>
                <div>https://reloadpi.com/<span>topups</span>/offer/<span>OFFER_ID</span>?ref=<span>YOUR_CODE</span></div>
                <div>https://reloadpi.com/<span>esims</span>/offer/<span>OFFER_ID</span>?ref=<span>YOUR_CODE</span></div>
              </div>
            </div>
            <div className="sdk-alert-warn" style={{ marginTop: 12 }}>
              <strong>⚠️ eSIM prices come in minor units.</strong> API fields like <code>price.fixed</code> or <code>priceAmount</code> are
              in the smallest currency unit (e.g. cents). Always divide by <code>priceCurrencyDivisor</code> (defaults to <code>100</code>)
              before displaying: <code style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--warn-text)" }}>{"const displayPrice = offer.price.fixed / (offer.priceCurrencyDivisor ?? 100);"}</code>
              {" "}A raw value of <code>3500</code> with divisor <code>100</code> → <strong>$35.00</strong>, not $3,500.
            </div>
            <div className="sdk-alert-info" style={{ marginTop: 12 }}>
              <strong>Referral tracking:</strong> when you generate links with <code>getBuyUrl()</code>, the <code>?ref=</code> parameter
              is handled automatically by ReloadPi. The checkout happens on ReloadPi — do not build your own checkout.
            </div>
            <div className="sdk-alert-info" style={{ marginTop: 12 }}>
              <strong>Commission:</strong> partners earn a share of the platform fee on completed referred purchases — up to 30% of the
              platform fee (default 15%). It is a percentage of the fee, not of the product value.
            </div>
          </Sec>

          <Sec id="modules" eyebrow="Step 8" title="Available modules">
            <div className="sdk-modules">
              <div className="sdk-module-card">
                <h3>🎟️ Vouchers</h3>
                <p>Gift card offers by brand, country, and filters.</p>
                <span className="sdk-method">searchOffers()</span>
                <span className="sdk-method">getOfferById()</span>
                <span className="sdk-method">listBrands()</span>
              </div>
              <div className="sdk-module-card">
                <h3>📱 Top-ups</h3>
                <p>Mobile top-up offers by country and operator.</p>
                <span className="sdk-method">searchOffers()</span>
                <span className="sdk-method">getOfferById()</span>
                <span className="sdk-method">listOperators()</span>
              </div>
              <div className="sdk-module-card">
                <h3>📶 eSIMs</h3>
                <p>eSIM plans and country-based browsing. Use <code>sdk.esims</code>.</p>
                <span className="sdk-method">searchPlans()</span>
                <span className="sdk-method">getPlanById()</span>
                <span className="sdk-method">listCountries()</span>
                <p style={{ marginTop: 8, fontSize: 12, color: "var(--warn-text)", lineHeight: 1.5 }}>
                  ⚠️ <strong>Price fields</strong> (<code>price</code>, <code>cost</code>) are in <strong>minor units</strong> (e.g. cents).
                  Always divide: <code>price / (priceCurrencyDivisor ?? 100)</code> before displaying.
                  Raw <code>3500</code> ÷ <code>100</code> = <strong>$35.00</strong>.
                </p>
                <p style={{ marginTop: 4, fontSize: 12, color: "var(--warn-text)", lineHeight: 1.5 }}>
                  ⚠️ Buy links: use <code>getBuyUrl("esims", ...)</code> — <strong>plural</strong>. Singular <code>"esim"</code> → 404.
                </p>
              </div>
            </div>
          </Sec>

          <Sec id="env" eyebrow="Step 9" title="Environment variables">
            <p>Recommended environment variables for a Next.js integration:</p>
            <CodeBlock language="env">{envExample}</CodeBlock>
            <div className="sdk-var-list">
              <div className="sdk-var-row"><code style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--accent)" }}>RELOADPI_API_KEY</code><span>— keep server-side only. Never in client bundles.</span></div>
              <div className="sdk-var-row"><code style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--accent)" }}>NEXT_PUBLIC_RELOADPI_SHOP_URL</code><span>— safe to expose, used for referral link generation.</span></div>
              <div className="sdk-var-row"><code style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--accent)" }}>NEXT_PUBLIC_RELOADPI_REFERRAL_CODE</code><span>— safe to expose for browser-visible referral flows.</span></div>
            </div>
          </Sec>

          <Sec id="access" eyebrow="Step 10" title="API access notes">
            <div className="sdk-alert-neutral" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p><strong>CORS:</strong> partner domains may need to be whitelisted before direct access is allowed. Use the SDK server-side to avoid CORS issues entirely.</p>
              <p><strong>Authentication:</strong> the SDK sends your API key on requests automatically. Keep the key on the server.</p>
              <p><strong>Header used:</strong> <code style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--accent)" }}>x-api-key</code></p>
            </div>
            <div className="sdk-alert-info">
              <strong>Platform note:</strong> <strong>ReloadPi</strong> is the consumer product. The underlying platform is <strong>Shoppi</strong>, operated by CrossLabs.
              The SDK hits <code>shoppi-backend.onrender.com</code> — this is intentional. The <code>shopUrl</code> config defaults to <code>https://reloadpi.com</code>
              so all referral links point to the consumer storefront correctly.
            </div>
          </Sec>

          <Sec id="limits" eyebrow="Step 11" title="Rate limits">
            <div className="sdk-limits-grid">
              <div className="sdk-limit-card">
                <div className="sdk-limit-card-label">Default partner limits</div>
                <div className="sdk-limit-row"><span>Monthly requests</span><span className="sdk-limit-val">10,000</span></div>
                <div className="sdk-limit-row"><span>Per minute</span><span className="sdk-limit-val">120</span></div>
                <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.75rem", lineHeight: 1.6 }}>
                  Expecting higher volume? Contact us before launch.
                </p>
              </div>
              <div className="sdk-limit-card">
                <div className="sdk-limit-card-label">Header sent by SDK</div>
                <div className="sdk-limit-row"><span>Auth header</span><span className="sdk-limit-val">x-api-key</span></div>
                <div className="sdk-limit-row"><span>Shop URL source</span><span className="sdk-limit-val">shopUrl config</span></div>
                <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.75rem", lineHeight: 1.6 }}>
                  Keys are scoped, rate-limited, and revocable at any time.
                </p>
              </div>
            </div>
          </Sec>

          <Sec id="troubleshooting" eyebrow="Step 12" title="Troubleshooting">
            <div className="sdk-alert-neutral">
              {[
                { code: "401", title: "Unauthorized",       desc: "API key is missing, invalid, or the partner account is inactive." },
                { code: "403", title: "Forbidden",          desc: "The requested catalog type is not included in your allowed access." },
                { code: "429", title: "Too many requests",  desc: "You reached your rate or monthly request limit." },
                { code: "CORS", title: "Blocked request",   desc: "Your domain may not be whitelisted. Use the SDK server-side." },
              ].map((e) => (
                <div key={e.code} className="sdk-error-row">
                  <span className="sdk-error-code">{e.code}</span>
                  <span className="sdk-error-title">{e.title}</span>
                  <div className="sdk-error-desc">{e.desc}</div>
                </div>
              ))}
            </div>
          </Sec>

          <Sec id="summary" eyebrow="Step 13" title="Quick summary">
            <div className="sdk-summary-inner">
              <p style={{ color: "var(--muted)", fontSize: "0.88rem" }}>The simplest way to think about this SDK:</p>
              <div className="sdk-summary-tagline">Fetch offers → show in your UI → redirect users to ReloadPi</div>
              <p style={{ fontSize: "0.85rem", color: "var(--muted)", lineHeight: 1.7 }}>
                The SDK handles authentication using your configured API key, and ReloadPi handles
                referral attribution when you generate buy links.
              </p>
              <div className="sdk-summary-actions">
                <Link href="/partner/apply" className="sdk-btn-primary">Become a partner</Link>
                <a href="https://crossabc.github.io/reloadpi-browse-sdk/" target="_blank" rel="noopener noreferrer" className="sdk-btn-secondary">Browse SDK docs →</a>
                <a href="#install" className="sdk-btn-secondary">Back to top</a>
              </div>
            </div>
          </Sec>

        </main>
      </div>

      {/* ── MOBILE SHEET ── */}
      {sheetOpen && (
        <div className="sdk-sheet-overlay" onClick={() => setSheetOpen(false)}>
          <div className="sdk-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sdk-sheet-header">
              <div>
                <div className="sdk-sheet-header-title">Contents</div>
                <div className="sdk-sheet-header-sub">Jump to any section</div>
              </div>
              <button className="sdk-sheet-close" onClick={() => setSheetOpen(false)}>Close</button>
            </div>
            <div className="sdk-sheet-nav">
              {sections.map((s, i) => (
                <a key={s.id} href={`#${s.id}`} className="sdk-sheet-link" onClick={() => setSheetOpen(false)}>
                  <span>{s.label}</span>
                  <span className="sdk-sheet-link-num">{String(i + 1).padStart(2, "0")}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <footer className="sdk-footer">
        <span>© 2025 CrossLabs · ReloadPi</span>
        <div>
          <a href="https://reloadpi.com" target="_blank" rel="noopener noreferrer">reloadpi.com</a>
          <a href="https://github.com/crossabc/reloadpi-browse-sdk" target="_blank" rel="noopener noreferrer">GitHub</a>
          <a href="https://www.npmjs.com/package/@crossabc/reloadpi-browse-sdk" target="_blank" rel="noopener noreferrer">npm</a>
          <a href="https://www.reddit.com/r/reloadpi" target="_blank" rel="noopener noreferrer">Reddit</a>
        </div>
      </footer>
    </div>
  );
}